import {
  useEffect,
  useRef,
  useState,
} from 'react';

import { v4 as uuidv4 } from 'uuid';

import {
  isSignatureToken,
  normalizeSignatureToken,
} from '../constants';

export default function useSignaturePlacement({
  defaultSignatures,
  pageNumber,
  setTypeAddSigner,
}) {
  const [menuPosition, setMenuPosition] = useState(null);
  const [showSigners, setShowSigners] = useState(false);
  const [showTypeSigners, setShowTypeSigners] = useState(false);
  const [signatures, setSignatures] = useState([]);
  const [draggingSignature, setDraggingSignature] = useState(null);
  const dragOffset = useRef({
    relative_position_x: 0,
    relative_position_y: 0,
  });
  const [resizingSignature, setResizingSignature] = useState(null);
  const resizeOffset = useRef({
    relative_position_x: 0,
    relative_position_y: 0,
  });
  const [dimensions, setDimensions] = useState({});

  const loadSignatures = () => {
    if (
      defaultSignatures &&
      Array.isArray(defaultSignatures.signers) &&
      defaultSignatures.signers.length > 0 &&
      defaultSignatures.dimensions &&
      typeof defaultSignatures.dimensions.x === 'number' &&
      typeof defaultSignatures.dimensions.y === 'number'
    ) {
      const loadedSigners = defaultSignatures.signers.map((signer) => {
        if (signer.sign && isSignatureToken(signer.sign)) {
          return {
            ...signer,
            sign: normalizeSignatureToken(signer.sign),
          };
        }

        return {
          ...signer,
          sign: undefined,
          relative_size_x:
            (signer.relative_size_x * defaultSignatures.dimensions.x) / 100,
          relative_size_y:
            (signer.relative_size_y * defaultSignatures.dimensions.y) / 100,
          relative_position_x:
            (signer.relative_position_x * defaultSignatures.dimensions.x) / 100,
          relative_position_y:
            (signer.relative_position_y * defaultSignatures.dimensions.y) / 100,
        };
      });
      setSignatures((prev) => [...prev, ...loadedSigners]);
      setDimensions(defaultSignatures.dimensions);
    }
  };

  const handleAssignSignature = (anchor, signer) => {
    setDimensions((previous) => ({
      x: previous.x || anchor.pageWidth,
      y: previous.y || anchor.pageHeight,
    }));

    setSignatures((previous) => {
      const withoutAnchor = previous.filter(
        (signature) => !(signature.sign === anchor.token
          && signature.page === anchor.page
          && signature.sign_occurrence === anchor.occurrence),
      );

      if (!signer) return withoutAnchor;

      return [
        ...withoutAnchor,
        {
          id: uuidv4(),
          name: signer.name,
          page: anchor.page,
          type: 'signature',
          signer_id: signer.id,
          sign: anchor.token,
          sign_occurrence: anchor.occurrence,
          relative_size_x: 14.7,
          relative_size_y: 5.05,
          relative_position_x: anchor.relative_position_x,
          relative_position_y: anchor.relative_position_y,
        },
      ];
    });
  };

  const preserveSignatures = (previousSignatures = [], anchors = []) => {
    const preserved = anchors.reduce((result, anchor) => {
      const previous = previousSignatures.find(
        (signature) => normalizeSignatureToken(signature.sign) === anchor.token
          && signature.sign_occurrence === anchor.occurrence,
      );

      if (!previous) return result;

      return [
        ...result,
        {
          ...previous,
          page: anchor.page,
          sign: anchor.token,
          sign_occurrence: anchor.occurrence,
          relative_position_x: anchor.relative_position_x,
          relative_position_y: anchor.relative_position_y,
        },
      ];
    }, []);

    if (anchors[0]) {
      setDimensions({
        x: anchors[0].pageWidth,
        y: anchors[0].pageHeight,
      });
    }
    setSignatures(preserved);
  };

  useEffect(() => {
    loadSignatures();
  }, []);

  //Muestra el menu de opciones de firma al hacer clic en el PDF
  const handlePdfClick = (event) => {
    if (draggingSignature !== null) return;

    const rect = event.target.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    if (dimensions.x === undefined || dimensions.y === undefined) {
      setDimensions({
        x: rect.width,
        y: rect.height,
      });
    }

    setMenuPosition({
      relative_position_x: x,
      relative_position_y: y,
    });
  };

  //Manejo de opciones del menú de firma
  const handleMenuOption = (option, signer = null) => {
    if (option === 'addSignature') {
      //Mostrar tipos de firmantes
      setShowTypeSigners(true);
      setShowSigners(false); // Mostrar opciones de firmantes
    } else if (option === 'cancel') {
      setMenuPosition(null);
      setShowTypeSigners(false);
      setShowSigners(false);
    } else if (option === 'selectTypeSigner') {
      setShowTypeSigners(false);
      setShowSigners(true);
      setTypeAddSigner(signer.id);
    } else if (option === 'selectSigner' && signer) {
      setSignatures((prev) => {
        return [
          ...prev,
          {
            page: pageNumber,
            type: 'signature',
            signer_id: signer.id, // Mantén una referencia al firmante
            relative_size_x: 90,
            relative_size_y: 40,
            relative_position_x: menuPosition.relative_position_x,
            relative_position_y: menuPosition.relative_position_y,
            id: uuidv4(), // Genera un identificador único para cada firma
            name: signer.name,
          },
        ];
      });

      setMenuPosition(null); // Ocultar el menú después de agregar la firma
      setShowSigners(false);
      setShowTypeSigners(false);
    }
  };

  //Eliminar firma
  const handleRemoveSignature = (id) => {
    setSignatures((prev) => prev.filter((signature) => signature.id !== id));

    setMenuPosition(null);
    setShowSigners(false);
    setShowTypeSigners(false);
  };

  //Manejo de arrastre de firma
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (draggingSignature !== null) {
        setSignatures((prevSignatures) =>
          prevSignatures.map((signature) =>
            signature.id === draggingSignature
              ? {
                ...signature,
                relative_position_x: e.clientX - dragOffset.current.relative_position_x,
                relative_position_y: e.clientY - dragOffset.current.relative_position_y,
              }
              : signature,
          ),
        );
      }
    };

    const handleMouseUp = () => {
      setDraggingSignature(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggingSignature]);

  //Manejo de redimensionamiento de firma
  const handleResizeMouseDown = (e, signatureId) => {
    e.stopPropagation();
    setResizingSignature(signatureId);
    resizeOffset.current = {
      relative_position_x: e.clientX,
      relative_position_y: e.clientY,
    };
  };

  //Manejo de redimensionamiento de firma
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (resizingSignature !== null) {
        setSignatures((prevSignatures) =>
          prevSignatures.map((signature) =>
            signature.id === resizingSignature
              ? {
                ...signature,
                relative_size_x: signature.sign
                  ? Math.max(2, signature.relative_size_x + ((e.clientX - resizeOffset.current.relative_position_x) / (dimensions.x || 1)) * 100)
                  : Math.max(90, signature.relative_size_x + (e.clientX - resizeOffset.current.relative_position_x)),
                relative_size_y: signature.sign
                  ? Math.max(2, signature.relative_size_y + ((e.clientY - resizeOffset.current.relative_position_y) / (dimensions.y || 1)) * 100)
                  : Math.max(40, signature.relative_size_y + (e.clientY - resizeOffset.current.relative_position_y)),
              }
              : signature,
          ),
        );
        resizeOffset.current = {
          relative_position_x: e.clientX,
          relative_position_y: e.clientY,
        };
      }
    };

    const handleMouseUp = () => {
      setResizingSignature(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizingSignature]);

  return {
    signatures,
    dimensions,
    menuPosition,
    showSigners,
    showTypeSigners,
    draggingSignature,
    dragOffset,
    setSignatures,
    setDraggingSignature,
    setShowSigners,
    setShowTypeSigners,
    handlePdfClick,
    handleMenuOption,
    handleRemoveSignature,
    handleResizeMouseDown,
    handleAssignSignature,
    preserveSignatures,
  };
}
